import { toDate, dateMin, dateMax } from '@ztechapps/common.utils'
import type { GanttTask, TimelineComputed, TimelineDay, ViewMode } from '../types'

export const parseIso = (iso: string, tz: string) => toDate(iso, tz)

const clampIso = (iso: string) => toDate(iso).toISOString()

export const computeRange = (
  tasks: GanttTask[],
  view: ViewMode,
  tz: string,
  startDate?: string,
  endDate?: string,
  columnWidth = 28,
  showWeekends = true
): TimelineComputed => {
  const taskStarts = tasks.map((t) => parseIso(t.start, tz))
  const taskEnds = tasks.map((t) => parseIso(t.end, tz))

  let start = startDate ? parseIso(startDate, tz) : taskStarts.length ? dateMin(taskStarts) : toDate(undefined, tz)
  let end = endDate ? parseIso(endDate, tz) : taskEnds.length ? dateMax(taskEnds) : toDate(undefined, tz)

  if (view === 'month') {
    start = start.clone().startOf('month')
    end = end.clone().endOf('month')
  } else if (view === 'week') {
    start = start.clone().startOf('week')
    end = end.clone().endOf('week')
  } else {
    start = start.clone().startOf('day')
    end = end.clone().endOf('day')
  }

  const days: TimelineDay[] = []
  const cursor = start.clone().startOf('day')
  const last = end.clone().startOf('day')
  while (cursor.isSameOrBefore(last)) {
    const isWeekend = [0, 6].includes(cursor.day())
    days.push({ iso: clampIso(cursor.toISOString()), isWeekend })
    cursor.add(1, 'day')
  }

  const totalDays = days.length
  const gridWidth = totalDays * columnWidth
  const startIso = clampIso(start.toISOString())
  const endIso = clampIso(end.toISOString())

  const toX = (iso: string) => {
    const m = parseIso(iso, tz)
    const d = m.diff(start, 'days', true)
    return Math.max(0, Math.min(gridWidth, d * columnWidth))
  }

  return { startIso, endIso, days: showWeekends ? days : days.map((d) => ({ ...d, isWeekend: false })), totalDays, columnWidth, gridWidth, toX }
}

export const durationDays = (startIso: string, endIso: string, tz: string) => {
  const s = parseIso(startIso, tz)
  const e = parseIso(endIso, tz)
  return Math.max(0, Math.ceil(e.endOf('day').diff(s.startOf('day'), 'days', true)))
}

export const formatDate = (iso: string, tz: string, locale?: string) => {
  const m = parseIso(iso, tz)
  if (locale) m.locale(locale)
  return m.format('M/D/YYYY')
}
